import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, ArrowRight } from "lucide-react";
import { waLink, IMAGES } from "../data/content";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "", innerClassName = "" }) => (
  <span className={`mask-line ${className}`}>
    <motion.span
      className={innerClassName}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollDown = () => {
    const el = document.getElementById("manifesto");
    if (el) window.__lenis?.scrollTo(el, { offset: -72 });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Villa mewah modern karya PT Raditya Konstruksi"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0E11]/70 via-[#0D0E11]/55 to-[#0D0E11]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0E11]/80 via-transparent to-transparent" />
      </motion.div>
      <div className="absolute inset-0 architect-grid opacity-60" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-14 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
          className="font-mono-arch text-[11px] sm:text-xs tracking-[0.35em] uppercase text-brass mb-6"
          data-testid="hero-eyebrow"
        >
          PT Raditya Konstruksi — Jakarta • Bali • Surabaya
        </motion.p>

        <h1 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-bone max-w-4xl" data-testid="hero-title">
          <MaskedLine delay={0.45}>Membangun Hunian</MaskedLine>
          <MaskedLine delay={0.6} innerClassName="italic brass-text-gradient">Mewah Standar Dunia,</MaskedLine>
          <MaskedLine delay={0.75}>Presisi Tanpa Kompromi.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.0 }}
          className="mt-7 max-w-xl text-sm sm:text-base text-ash font-light leading-relaxed"
          data-testid="hero-subtitle"
        >
          Kontraktor spesialis rumah mewah: desain &amp; bangun, renovasi masterpiece,
          hingga interior bespoke. Satu tim, satu standar — mahakarya.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-btn"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-wa hover:bg-wahover text-obsidian font-mono-arch text-xs tracking-[0.18em] uppercase px-7 py-4 transition-colors duration-300 wa-pulse"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.2} />
            Konsultasi via WhatsApp
          </a>
          <button
            data-testid="hero-portfolio-btn"
            onClick={scrollDown}
            className="group inline-flex items-center gap-3 border border-[rgba(197,160,89,0.4)] hover:border-gold text-gold font-mono-arch text-xs tracking-[0.18em] uppercase px-7 py-4 transition-colors duration-300"
          >
            Lihat Portofolio
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.5 }}
          className="mt-16 pt-8 border-t border-white/10 grid grid-cols-3 gap-6 max-w-2xl"
          data-testid="hero-metrics"
        >
          {[
            ["120+", "Proyek Selesai"],
            ["15 Th", "Pengalaman"],
            ["10 Th", "Garansi Struktur"],
          ].map(([v, l]) => (
            <div key={l}>
              <p className="font-serif-lux text-3xl sm:text-4xl text-gold">{v}</p>
              <p className="font-mono-arch text-[10px] tracking-[0.25em] uppercase text-dim mt-1">{l}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden lg:flex flex-col items-center gap-3 text-dim hover:text-gold transition-colors"
        aria-label="Gulir ke bawah"
      >
        <span className="font-mono-arch text-[10px] tracking-[0.3em] uppercase [writing-mode:vertical-lr]">Gulir</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
};
